import { Component, Emit, Prop, Ref, Vue } from 'vue-property-decorator'
import VtInput from '@/components/common/VtInput.vue'
import { auth } from '@/api/auth'
import sessionStore from '@/session/sessionStore.js'
import apiExceptionType from '@/utils/apiExceptionType'
import {
    initialLoginLayoutChangePasswordState,
    LoginLayoutChangePasswordState
} from '@/views/auth/components/login/login-layout-change-password/login-layout-change-password.model'
import { authService } from '@/api/services/auth/auth.service'
import { ChangePasswordTypeEnum } from '@/views/auth/components/login/login-layout/login-layout.model'
import { StoreUtils } from '@/shared/util/store-utils'

@Component
export default class LoginLayoutChangePassword extends Vue {
    @Ref() username!: typeof VtInput

    @Prop({ type: String, required: true })
    changePasswordType!: ChangePasswordTypeEnum

    get passwordMinimumLength(): number {
        return this.$store.getters.parameters.global?.passwordMinimumLength ?? 0
    }

    get passwordMaximumLength(): number {
        return this.$store.getters.parameters.global?.passwordMaximumLength ?? 0
    }
    get hasError(): boolean {
        return !!this.state.error
    }

    get hasSuccess(): boolean {
        return !!this.state.success
    }
    get modelValidated(): boolean {
        const hasErrors: boolean = this.$validator.errors.any()
        const isDirty: boolean = this.$_someFieldIsDirty(this.fields)
        return !hasErrors && isDirty && !this.state.busy
    }

    state: LoginLayoutChangePasswordState
    constructor() {
        super()
        this.state = initialLoginLayoutChangePasswordState()
    }

    async created(): Promise<void> {
        await sessionStore.loadGlobalParameters()
    }

    @Emit('show-login')
    showLogin(): void {}

    togglePassword(): void {
        this.state.showPassword = !this.state.showPassword
    }

    private _checkNewPassword(password: string): string {
        if (password.length) {
            if (password.length < this.passwordMinimumLength) {
                return this.$tc('userConfiguration.passwordMinimumLengthError', 1, { minimumLength: this.passwordMinimumLength })
            }
            if (password.length > this.passwordMaximumLength) {
                return this.$tc('userConfiguration.passwordMaximumLengthError', 1, { maximumLength: this.passwordMaximumLength })
            }
        } else {
            return this.$tc('userConfiguration.emptyNewPassword')
        }
        return ''
    }

    private _validPasswords(): boolean {
        if (this.state.password !== this.state.passwordConfirmation) {
            this.state.error = this.$tc('userConfiguration.newPasswordMismatch')
        } else {
            this.state.error = this._checkNewPassword(this.state.password)
        }
        return !this.state.error
    }

    private async _changePasswordAfterFirstLogin(): Promise<void> {
        const response =
            await this.$_toCallUnique(
                authService.changePasswordAfterFirstLogin({ newPassword: this.state.password }),
                (error) => this._changePasswordFailed(error))
        if (response !== null) {
            await this._changePasswordOk()
        }
    }
    private async _recoveryChangePassword(): Promise<void> {
        const response =
                await this.$_toCallUnique(
                    authService.recoveryChangePassword({ newPassword: this.state.password }),
                    (error) => this._changePasswordFailed(error))
        if (response) {
            await auth.updateSessionStorage(response, false)

            if (!response.passwordSingleUse) {
                await this._changePasswordOk()
            }
        }
    }
    async changePassword(): Promise<void> {
        if (this._validPasswords()) {
            if (this.changePasswordType === ChangePasswordTypeEnum.RECOVERY) {
                await this._recoveryChangePassword()
            } else if (this.changePasswordType === ChangePasswordTypeEnum.CHANGE_PASSWORD_AFTER_FIRST_LOGIN) {
                await this._changePasswordAfterFirstLogin()
            }
        }
    }

    private async _changePasswordOk(): Promise<void> {
        await sessionStore.update()

        // Validations
        if (!this.$store.getters.laboratoriesConfiguration.length) {
            // No operation configured to any laboratory
            this.state.error = this.$tc('userConfiguration.noOperationsError')
        } else if (!this.$store.getters.modules.length) {
            // No access to any laboratory
            this.state.error = this.$tc('userConfiguration.noModulesError')
        }
        if (this.state.error) {
            this.state.success = this.$tc('userConfiguration.successfulPasswordChange')
            this.state.busy = true // disable submit button
            auth.clearSession()
            return
        }

        setTimeout(() => StoreUtils.updateUserLoggedIn(true), 500)
        this.$router.push({ name: 'home' })
    }

    private _changePasswordFailed(error: any): void {
        if (error?.response?.data) {
            const data = error.response.data
            if (data.messageTranslated) {
                this.state.error = data.message
                return
            } else {
                switch (data.code) {
                case apiExceptionType.INVALID_PASSWORD:
                    this.state.error = this.$tc('login.error.invalidPassword')
                    return
                case apiExceptionType.REPEATED_PASSWORD:
                    this.state.error = this.$tc('login.error.passwordPreviouslyUsed')
                    return
                }
            }
        }

        this.$_showErrorModal()
    }
}
